// Account Statement PDF generator (Turkish, Roboto via pdfmake).
// Used for both Customer (Müşteri) and Vendor (Tedarikçi) cari ekstresi.
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { fmtTRY, fmtDate } from "./format";

// Register built-in Roboto fonts (full Turkish support).
pdfMake.vfs = pdfFonts.vfs || (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) || pdfFonts;

const slugify = (s) =>
  String(s || "Cari")
    .replace(/ı/g, "i").replace(/İ/g, "I")
    .replace(/ş/g, "s").replace(/Ş/g, "S")
    .replace(/ğ/g, "g").replace(/Ğ/g, "G")
    .replace(/ç/g, "c").replace(/Ç/g, "C")
    .replace(/ö/g, "o").replace(/Ö/g, "O")
    .replace(/ü/g, "u").replace(/Ü/g, "U")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Per-kind labels for Customer vs Vendor statements.
const KIND_CONFIG = {
  customer: {
    title: "MÜŞTERİ CARİ EKSTRESİ",
    partyKeyLabel: "Müşteri",
    rowTypeLabel: (r) => (r.type === "sale" ? "Satış" : "Tahsilat"),
    creditTotalLabel: "Toplam Alacak",
    finalBalanceLabel: "Güncel Bakiye",
    positiveBalanceNote: "Müşterinin bakiye borcu (size olan alacağınız)",
    negativeBalanceNote: "Avans / fazla tahsilat",
  },
  vendor: {
    title: "TEDARİKÇİ CARİ EKSTRESİ",
    partyKeyLabel: "Tedarikçi",
    rowTypeLabel: (r) => (r.type === "purchase" ? "Satın Alma" : "Ödeme"),
    creditTotalLabel: "Toplam Ödeme",
    finalBalanceLabel: "Güncel Cari Bakiye",
    positiveBalanceNote: "Tedarikçiye olan borcunuz",
    negativeBalanceNote: "Fazla ödeme / avans",
  },
};

function _buildStatementDoc({ kind, party, rows, totalDebit, totalCredit, finalBalance }) {
  const cfg = KIND_CONFIG[kind];
  const today = new Date();
  const generatedAt =
    `${String(today.getDate()).padStart(2, "0")}.` +
    `${String(today.getMonth() + 1).padStart(2, "0")}.` +
    `${today.getFullYear()} ` +
    `${String(today.getHours()).padStart(2, "0")}:` +
    `${String(today.getMinutes()).padStart(2, "0")}`;

  const headerInfo = [
    [cfg.partyKeyLabel, party?.name || "—"],
    ["Yetkili Kişi", party?.contact_person || "—"],
    ["Telefon", party?.phone || "—"],
    ["E-Posta", party?.email || "—"],
    ["Ekstre Tarihi", generatedAt],
  ];

  const tableBody = [
    [
      { text: "Tarih", style: "th" },
      { text: "İşlem Tipi", style: "th" },
      { text: "Açıklama", style: "th" },
      { text: "Borç", style: "th", alignment: "right" },
      { text: "Alacak", style: "th", alignment: "right" },
      { text: "Bakiye", style: "th", alignment: "right" },
    ],
    ...rows.map((r) => [
      { text: r.date ? fmtDate(r.date) : "—", style: "td" },
      { text: cfg.rowTypeLabel(r), style: "td" },
      { text: r.description || "—", style: "td" },
      { text: r.debit > 0 ? fmtTRY(r.debit) : "—", style: "td", alignment: "right" },
      { text: r.credit > 0 ? fmtTRY(r.credit) : "—", style: "td", alignment: "right" },
      { text: fmtTRY(r.balance), style: "tdBold", alignment: "right" },
    ]),
    [
      { text: "TOPLAM", style: "tfoot", colSpan: 3, alignment: "right" },
      {}, {},
      { text: fmtTRY(totalDebit), style: "tfoot", alignment: "right" },
      { text: fmtTRY(totalCredit), style: "tfoot", alignment: "right" },
      { text: fmtTRY(finalBalance), style: "tfoot", alignment: "right" },
    ],
  ];

  const balanceLabel =
    finalBalance > 0.0001
      ? cfg.positiveBalanceNote
      : finalBalance < -0.0001
      ? cfg.negativeBalanceNote
      : "Bakiye sıfır";

  return {
    pageSize: "A4",
    pageMargins: [40, 50, 40, 50],
    info: {
      title: `${party?.name || cfg.partyKeyLabel} - Cari Ekstresi`,
      author: "Muhasebe & Müşteri Yönetimi",
    },
    content: [
      { text: cfg.title, style: "title" },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#0f172a" }],
        margin: [0, 4, 0, 14],
      },
      {
        table: {
          widths: ["28%", "*"],
          body: (headerInfo ?? []).map(([k, v]) => [
            { text: k, style: "infoKey" },
            { text: v, style: "infoVal" },
          ]),
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingTop: () => 3,
          paddingBottom: () => 3,
        },
        margin: [0, 0, 0, 16],
      },
      { text: `Cari Hareket Dökümü (${(rows ?? []).length} kayıt)`, style: "sectionTitle", margin: [0, 4, 0, 6] },
      {
        table: {
          headerRows: 1,
          widths: [60, 60, "*", 70, 70, 75],
          body: tableBody,
        },
        layout: {
          fillColor: (rowIndex) =>
            rowIndex === 0 ? "#f1f5f9" : rowIndex === (tableBody ?? []).length - 1 ? "#f8fafc" : null,
          hLineColor: () => "#e2e8f0",
          vLineColor: () => "#e2e8f0",
          hLineWidth: (i, node) =>
            i === 0 || i === 1 || i === (node.table.body ?? []).length || i === (node.table.body ?? []).length - 1
              ? 0.8
              : 0.4,
          vLineWidth: () => 0.4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
      },
      {
        margin: [0, 18, 0, 0],
        columns: [
          { width: "*", text: "" },
          {
            width: 260,
            table: {
              widths: ["*", 100],
              body: [
                [
                  { text: "Toplam Borç", style: "summKey" },
                  { text: fmtTRY(totalDebit), style: "summVal", alignment: "right" },
                ],
                [
                  { text: cfg.creditTotalLabel, style: "summKey" },
                  { text: fmtTRY(totalCredit), style: "summVal", alignment: "right" },
                ],
                [
                  { text: cfg.finalBalanceLabel, style: "summKeyBold" },
                  { text: fmtTRY(finalBalance), style: "summValBold", alignment: "right" },
                ],
              ],
            },
            layout: {
              hLineColor: () => "#cbd5e1",
              vLineColor: () => "#cbd5e1",
              hLineWidth: (i, node) => (i === 0 || i === (node.table.body ?? []).length ? 1 : 0.4),
              vLineWidth: () => 0,
              paddingTop: () => 5,
              paddingBottom: () => 5,
            },
          },
        ],
      },
      { text: balanceLabel, style: "footnote", margin: [0, 6, 0, 0] },
      {
        text:
          "Bu ekstre yukarıda belirtilen tarihte sistem üzerinden otomatik olarak oluşturulmuştur. " +
          "Mutabakat için tarafımızla iletişime geçebilirsiniz.",
        style: "footnote",
        margin: [0, 14, 0, 0],
      },
    ],
    styles: {
      title: { fontSize: 18, bold: true, color: "#0f172a", alignment: "left", letterSpacing: 1 },
      sectionTitle: { fontSize: 11, bold: true, color: "#334155" },
      infoKey: { fontSize: 10, color: "#64748b" },
      infoVal: { fontSize: 10, color: "#0f172a", bold: true },
      th: { fontSize: 9, bold: true, color: "#0f172a", fillColor: "#f1f5f9" },
      td: { fontSize: 9, color: "#1e293b" },
      tdBold: { fontSize: 9, color: "#0f172a", bold: true },
      tfoot: { fontSize: 9.5, bold: true, color: "#0f172a", fillColor: "#f8fafc" },
      summKey: { fontSize: 10, color: "#475569" },
      summVal: { fontSize: 10, color: "#0f172a" },
      summKeyBold: { fontSize: 11, bold: true, color: "#0f172a" },
      summValBold: { fontSize: 11, bold: true, color: "#0f172a" },
      footnote: { fontSize: 8.5, italics: true, color: "#64748b" },
    },
    defaultStyle: { font: "Roboto" },
  };
}

export function generateCustomerStatementPdf({ customer, rows, totalDebit, totalCredit, finalBalance }) {
  const doc = _buildStatementDoc({
    kind: "customer",
    party: customer,
    rows,
    totalDebit,
    totalCredit,
    finalBalance,
  });
  pdfMake.createPdf(doc).download(`${slugify(customer?.name)}_Cari_Ekstresi.pdf`);
}

export function generateVendorStatementPdf({ vendor, rows, totalDebit, totalCredit, finalBalance }) {
  const doc = _buildStatementDoc({
    kind: "vendor",
    party: vendor,
    rows,
    totalDebit,
    totalCredit,
    finalBalance,
  });
  pdfMake.createPdf(doc).download(`${slugify(vendor?.name)}_Cari_Ekstresi.pdf`);
}
